import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { COMPANY, SERVIZI, SETTORI, VALORI, NUMERI, MEDIA } from "@/data/content";
import { MaskedLines, Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Kinetic";

const HERO_DELAY = 0.15;
const LINE_STAGGER = 0.08;

/* -------------------------------------------------------------------------- */
/*  HERO — editorial light, corporate imagery, split composition              */
/* -------------------------------------------------------------------------- */
function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-4%"]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-background text-foreground"
      data-testid="hero-section"
    >
      {/* Subtle grain background */}
      <div aria-hidden className="absolute inset-0 grain -z-10" />

      {/* Rotated vertical label — brand mark */}
      <div className="hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 vlabel text-foreground/50 z-10">
        XL Brokers — {new Date().getFullYear()}
      </div>

      <motion.div
        className="container-x pt-36 pb-16 sm:pt-40 sm:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 relative z-10 items-end"
        style={reduce ? {} : { y: textY }}
      >
        {/* LEFT — headline + copy + CTAs */}
        <div className="md:col-span-7">
          <motion.span
            className="overline block"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: HERO_DELAY, duration: 1 }}
          >
            Consulenza indipendente
          </motion.span>

          <h1 className="mt-8 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.02] tracking-tight text-foreground">
            <MaskedLines delay={HERO_DELAY} stagger={LINE_STAGGER}>
              <>Coperture assicurative</>
              <>
                <span className="italic text-primary">su misura</span> per chi
              </>
              <>non accetta compromessi.</>
            </MaskedLines>
          </h1>

          <motion.p
            className="mt-10 lead max-w-xl"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: HERO_DELAY + 0.55, duration: 0.9, ease: [0.22, 0.9, 0.24, 1] }}
          >
            XL Brokers è una società di brokeraggio assicurativo indipendente. Affianchiamo PMI, professionisti e famiglie nella costruzione di programmi di protezione personalizzati, con la cura di un consulente privato.
          </motion.p>

          <motion.div
            className="mt-12 flex flex-wrap gap-4"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: HERO_DELAY + 0.75, duration: 0.9, ease: [0.22, 0.9, 0.24, 1] }}
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-preventivo"
              className="btn-base bg-foreground text-background px-8 py-4 hover:bg-primary group"
            >
              Richiedi un preventivo
              <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
            </Link>
            <Link
              to="/servizi"
              data-testid="hero-cta-aiuto"
              className="btn-base border border-foreground/25 text-foreground px-8 py-4 hover:bg-foreground hover:text-background"
            >
              Come possiamo aiutarti
            </Link>
          </motion.div>
        </div>

        {/* RIGHT — corporate photograph with clipped frame + parallax */}
        <motion.div
          className="md:col-span-5 relative"
          initial={reduce ? false : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: HERO_DELAY + 0.35, duration: 1, ease: [0.22, 0.9, 0.24, 1] }}
        >
          <div className="relative aspect-[3/4] frame-clip spotlight overflow-hidden bg-muted">
            <motion.img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=85"
              alt="Consulenza corporate XL Brokers"
              className="absolute inset-0 h-[115%] w-full object-cover"
              style={reduce ? {} : { y: imageY, scale: imageScale }}
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent" />
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: HERO_DELAY + 1.1, duration: 1.2 }}
      >
        <span className="text-[10px] uppercase tracking-[0.36em] text-foreground/50">
          Scroll
        </span>
        <motion.span
          className="h-10 w-px bg-foreground/40 origin-top"
          animate={reduce ? {} : { scaleY: [0.2, 1, 0.2] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  NUMERI — with entrance count                                              */
/* -------------------------------------------------------------------------- */
function Numeri() {
  return (
    <section className="relative bg-foreground text-background py-20 overflow-hidden" data-testid="numeri-section">
      <div className="noise-overlay" />
      <div className="container-x">
        <StaggerGroup className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6" stagger={0.1}>
          {NUMERI.map((n) => (
            <StaggerItem key={n.label} y={30}>
              <div data-testid={`kpi-${n.label.slice(0, 6)}`}>
                <p className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tight text-secondary leading-none">
                  {n.kpi}
                </p>
                <p className="mt-4 text-[10px] uppercase tracking-[0.32em] text-background/55 max-w-[14rem]">
                  {n.label}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  MANIFESTO — numbered chapters with sticky serif numerals                  */
/* -------------------------------------------------------------------------- */
const CHAPTERS = [
  {
    n: "01",
    kicker: "Indipendenza",
    title: "Un solo interesse:\nil tuo.",
    body:
      "Non lavoriamo per una compagnia, lavoriamo per te. Selezioniamo le coperture nel mercato italiano ed internazionale con un'unica bussola: la tua esposizione reale al rischio.",
  },
  {
    n: "02",
    kicker: "Metodo sartoriale",
    title: "Un programma\ncucito addosso.",
    body:
      "Non vendiamo prodotti standard. Studiamo l'azienda, la famiglia, il patrimonio. Poi negoziamo wording su misura, franchigie sostenibili e clausole che ti proteggono davvero quando serve.",
  },
  {
    n: "03",
    kicker: "Presenza",
    title: "Sempre\nal tuo fianco.",
    body:
      "Non ci limitiamo a vendere una polizza: rimaniamo al tuo fianco tutto l'anno. Rivediamo insieme il programma quando cambia la tua realtà e ti supportiamo passo dopo passo nella gestione di ogni sinistro, dalla denuncia alla liquidazione. Perché il vero valore di un broker si misura quando succede qualcosa.",
  },
  {
    n: "04",
    kicker: "Ascolto",
    title: "Prima capire,\npoi proporre.",
    body:
      "Il preventivo arriva solo dopo aver ascoltato la tua realtà. Un incontro dedicato per conoscere l'azienda, il patrimonio e gli obiettivi — poi costruiamo la proposta su misura, mai prima. Perché una buona copertura nasce sempre da una buona conversazione.",
  },
];

function Manifesto() {
  return (
    <section className="section-y bg-background" data-testid="manifesto-section">
      <div className="container-x">
        <Reveal className="max-w-4xl">
          <span className="overline">Manifesto</span>
          <h2 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Quattro convinzioni,<br />
            <span className="italic text-primary">un solo mestiere.</span>
          </h2>
        </Reveal>

        <div className="mt-24 space-y-24 sm:space-y-32">
          {CHAPTERS.map((c, i) => (
            <div
              key={c.n}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
              data-testid={`manifesto-chapter-${c.n}`}
            >
              {/* Big serif chapter numeral (sticky on desktop) */}
              <div className="md:col-span-5 lg:col-span-6">
                <div className="md:sticky md:top-32">
                  <Reveal y={40} duration={1.1}>
                    <div className="flex items-baseline gap-6">
                      <span
                        className="chapter-num chapter-num--filled text-[9rem] sm:text-[12rem] lg:text-[16rem] text-primary/25"
                        aria-hidden
                      >
                        {c.n}
                      </span>
                      <span className="text-xs uppercase tracking-[0.32em] text-primary font-semibold pb-3">
                        {c.kicker}
                      </span>
                    </div>
                  </Reveal>
                </div>
              </div>

              {/* Chapter body */}
              <div className="md:col-span-7 lg:col-span-6 md:pt-8">
                <Reveal delay={0.05}>
                  <h3 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight whitespace-pre-line">
                    {c.title}
                  </h3>
                </Reveal>
                <Reveal delay={0.15}>
                  <p className="mt-8 text-lg text-muted-foreground leading-relaxed max-w-xl">
                    {c.body}
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <div className="mt-10 h-px w-24 bg-primary/60" />
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  VALORI — quick grid                                                       */
/* -------------------------------------------------------------------------- */
function Valori() {
  return (
    <section className="section-y container-x" data-testid="valori-section">
      <Reveal className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-6">
          <span className="overline">Valori</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Le quattro <span className="italic text-primary">costanti</span><br />del nostro lavoro
          </h2>
        </div>
        <p className="md:col-span-5 md:col-start-8 text-muted-foreground text-lg leading-relaxed">
          Piccoli principi, applicati con disciplina. È da qui che nasce la fiducia che i nostri clienti ci accordano da oltre dieci anni.
        </p>
      </Reveal>

      <StaggerGroup className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-foreground/10 border border-foreground/10">
        {VALORI.map((v, i) => (
          <StaggerItem
            key={v.titolo}
            className="bg-background p-8 sm:p-10 group relative hover:bg-muted/40 transition-colors duration-500"
          >
            <div data-testid={`valore-${i}`}>
              <p className="font-display italic text-5xl text-primary/25 leading-none group-hover:text-secondary/70 transition-colors duration-500">
                0{i + 1}
              </p>
              <h3 className="mt-8 font-display text-2xl">{v.titolo}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              <ArrowUpRight className="absolute top-8 right-8 h-4 w-4 text-foreground/20 group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  SOCIETÀ — real photography with clipped frame + spotlight                 */
/* -------------------------------------------------------------------------- */
function Studio() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="relative section-y bg-muted/50 overflow-hidden" data-testid="studio-section">
      <div className="container-x grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6 relative">
          <div className="relative aspect-[4/5] frame-clip spotlight overflow-hidden">
            <motion.img
              src={MEDIA.about}
              alt="Sede XL Brokers"
              className="absolute inset-0 h-[120%] w-full object-cover"
              style={reduce ? {} : { y: imgY }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/25 via-transparent to-transparent" />
          </div>
          {/* Floating tag */}
          <Reveal className="absolute -bottom-6 -right-4 sm:right-6 bg-foreground text-background px-6 py-4 shadow-2xl">
            <p className="text-[10px] uppercase tracking-[0.32em] text-secondary">
              Società · Bologna
            </p>
            <p className="mt-1 font-display italic text-xl">XL Brokers</p>
          </Reveal>
        </div>

        <div className="md:col-span-5 md:col-start-8">
          <Reveal>
            <span className="overline">La società</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Un <span className="italic text-primary">atelier</span><br />del rischio.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-muted-foreground leading-relaxed">
              A Bologna, in via di Corticella, un team ridotto e specializzato lavora con imprenditori, professionisti e famiglie del territorio. Nessuna filiera commerciale: solo consulenza tecnica, letta e firmata da chi ti conosce per nome.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/chi-siamo"
              data-testid="studio-cta"
              className="mt-10 inline-flex items-center gap-2 text-sm font-medium underline-link"
            >
              Conosci la società <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  SERVIZI — dark grid                                                        */
/* -------------------------------------------------------------------------- */
function Servizi() {
  return (
    <section className="relative bg-foreground text-background section-y overflow-hidden" data-testid="servizi-section">
      <div className="noise-overlay" />
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-6">
            <span className="overline text-secondary">Servizi</span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Come possiamo<br /><span className="italic text-secondary">aiutarti</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5 md:col-start-8">
            <p className="text-background/70 text-lg leading-relaxed">
              Quattro pilastri di servizio che si combinano in un programma unico, calibrato sul tuo profilo.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="mt-16 grid md:grid-cols-2 gap-px bg-background/10 border border-background/10">
          {SERVIZI.map((s, i) => (
            <StaggerItem key={s.slug}>
              <Link
                to={`/servizi#${s.slug}`}
                data-testid={`servizio-card-${s.slug}`}
                className="block bg-foreground hover:bg-[#0c2638] transition-colors duration-500 p-10 sm:p-14 group h-full"
              >
                <div className="flex items-start justify-between">
                  <span className="text-secondary font-display text-lg tabular-nums">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-background/40 group-hover:text-secondary group-hover:rotate-45 transition-all duration-500" />
                </div>
                <h3 className="mt-10 font-display text-3xl sm:text-4xl">{s.title}</h3>
                <p className="mt-5 text-background/65 leading-relaxed">{s.short}</p>
                <div className="mt-8 h-px w-0 group-hover:w-24 bg-secondary transition-all duration-700" />
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  SETTORI — editorial list                                                  */
/* -------------------------------------------------------------------------- */
function Settori() {
  return (
    <section className="section-y container-x" data-testid="settori-section">
      <div className="grid md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-4">
          <span className="overline">Settori</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Otto aree<br />di <span className="italic text-primary">specializzazione</span>
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Costruite negli anni con il dialogo costante con imprenditori, professionisti e famiglie.
          </p>
          <Link to="/contatti" data-testid="settori-cta" className="mt-10 inline-flex items-center gap-2 text-sm font-medium underline-link">
            Parla con un consulente <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <div className="md:col-span-8">
          <ul className="divide-y divide-foreground/10 border-t border-b border-foreground/10">
            {SETTORI.map((s, i) => (
              <li key={s.slug}>
                <Reveal y={16} amount={0.4}>
                  <Link
                    to={`/settori/${s.slug}`}
                    data-testid={`settore-row-${s.slug}`}
                    className="grid grid-cols-12 items-center py-7 group hover:pl-4 transition-all duration-500"
                  >
                    <span className="col-span-2 text-xs text-muted-foreground tabular-nums tracking-widest">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="col-span-8 font-display text-2xl sm:text-3xl md:text-4xl tracking-tight group-hover:italic group-hover:text-primary transition-all duration-500">
                      {s.title}
                    </span>
                    <span className="col-span-2 flex justify-end">
                      <ArrowUpRight className="h-5 w-5 text-foreground/40 group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                    </span>
                  </Link>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  CTA — final                                                                */
/* -------------------------------------------------------------------------- */
function CTA() {
  return (
    <section className="relative section-y bg-background overflow-hidden" data-testid="final-cta-section">
      <div className="container-x grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <Reveal><span className="overline">Iniziamo</span></Reveal>
          <h2 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            <MaskedLines stagger={0.08}>
              <>Un&apos;analisi</>
              <>
                <span className="italic text-primary">su misura</span>,
              </>
              <>in 48 ore.</>
            </MaskedLines>
          </h2>
          <Reveal delay={0.4}>
            <p className="mt-8 lead max-w-xl">
              Raccontaci la tua attività: ti restituiremo un quadro completo dei rischi ed una prima proposta di programma assicurativo, senza impegno.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="md:col-span-5 md:justify-self-end">
          <Link
            to="/contatti"
            data-testid="footer-cta-richiedi"
            className="btn-base bg-foreground text-background px-10 py-5 hover:bg-primary group"
          >
            Richiedi una consulenza
            <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
          </Link>
          <div className="mt-12 border-t border-foreground/10 pt-6 min-w-[16rem]">
            <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">oppure scrivici a</p>
            <a
              href={`mailto:${COMPANY.email}`}
              className="mt-3 inline-block font-display italic text-2xl underline-link"
              data-testid="home-cta-email"
            >
              {COMPANY.email}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page composition                                                          */
/* -------------------------------------------------------------------------- */
export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <Numeri />
      <Manifesto />
      <Valori />
      <Studio />
      <Servizi />
      <Settori />
      <CTA />
    </div>
  );
}
